import { useFlags } from '@insidedesk/tuxedo';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface TooltipContextType {
  tooltipsEnabled: boolean;
  toggleTooltips: () => void;
}

const TooltipContext = createContext<TooltipContextType | undefined>(undefined);

interface TooltipProviderProps {
  children: ReactNode;
}

export function TooltipProvider({ children }: TooltipProviderProps) {
  const flags = useFlags();
  const [userTooltipsEnabled, setUserTooltipsEnabled] = useState<boolean>(
    () => {
      // Load from localStorage, default to true
      const stored = localStorage.getItem('moneypenny-tooltips-enabled');
      if (stored !== null) {
        try {
          return JSON.parse(stored);
        } catch {
          // If JSON parsing fails, fall back to default value
          return true;
        }
      }
      return true;
    },
  );

  const toggleTooltips = useCallback(() => {
    setUserTooltipsEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem(
        'moneypenny-tooltips-enabled',
        JSON.stringify(newValue),
      );
      return newValue;
    });
  }, []);

  const tooltipsEnabled = userTooltipsEnabled;

  const contextValue = useMemo(
    () => ({ tooltipsEnabled, toggleTooltips }),
    [tooltipsEnabled, toggleTooltips],
  );

  return (
    <TooltipContext.Provider value={contextValue}>
      {children}
    </TooltipContext.Provider>
  );
}

export function useTooltipSettings() {
  const context = useContext(TooltipContext);
  if (context === undefined) {
    throw new Error('useTooltipSettings must be used within a TooltipProvider');
  }
  return context;
}
